import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { RevenueData } from "../../../api/types/analytics.types";
import { chartTheme } from "../../../config/chartTheme";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useDebounce } from "../../../hooks/useDebounce";
import { getDateTicks } from "../../../utils/chartUtils";
import styles from "./Chart.module.scss";

interface RevenueChartProps {
  data: RevenueData[];
  currency?: string;
}

const RevenueChart = ({ data, currency = "AZN" }: RevenueChartProps) => {
  const { t } = useTranslation("translation");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 768);

  const handleResize = useDebounce(() => {
    setIsMobile(window.innerWidth <= 576);
    setIsTablet(window.innerWidth <= 768);
  }, 150);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  const formatCurrency = (value: number) => {
    return `${value.toFixed(2)} ${currency}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Calculate evenly spaced ticks for XAxis
  const getTicks = () => getDateTicks(data);

  return (
    <div className={styles.chartContainer}>
      <h3 className={styles.chartTitle}>
        {t("charts.revenue.title") || "Revenue & Net Profit"}
      </h3>
      <ResponsiveContainer
        width="100%"
        height={350}
        className={styles.chartWrapper}
      >
        <LineChart
          data={data}
          margin={{
            top: 5,
            right: isMobile ? 5 : 15,
            left: isMobile ? 0 : 5,
            bottom: isMobile ? 70 : isTablet ? 60 : 50,
          }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={chartTheme.colors.grid}
          />
          <XAxis
            dataKey="date"
            stroke={chartTheme.colors.text}
            style={{
              fontFamily: chartTheme.font.family,
              fontSize: isMobile ? 10 : chartTheme.font.sizes.small,
            }}
            tickFormatter={formatDate}
            ticks={getTicks()}
            domain={[data[0]?.date || "", data[data.length - 1]?.date || ""]}
            interval={0}
            angle={isMobile ? -60 : -45}
            textAnchor="end"
            height={isMobile ? 70 : isTablet ? 60 : 50}
          />
          <YAxis
            stroke={chartTheme.colors.text}
            style={{
              fontFamily: chartTheme.font.family,
              fontSize: isMobile ? 10 : chartTheme.font.sizes.small,
            }}
            width={isMobile ? 50 : 40}
            tickFormatter={(value) => {
              // Handle very small values
              if (value < 1) {
                return value.toFixed(2);
              }
              if (value >= 1000) {
                const kValue = value / 1000;
                // For values >= 10K, show whole numbers
                if (kValue >= 10) {
                  return `${Math.round(kValue)}K`;
                }
                // For values < 10K, show one decimal
                return `${kValue.toFixed(1)}K`;
              }
              // For values between 1 and 1000, show as is
              return Math.round(value).toString();
            }}
          />
          <Tooltip
            contentStyle={{
              background: chartTheme.colors.background,
              border: `1px solid ${chartTheme.colors.border}`,
              borderRadius: chartTheme.borderRadius.tooltip,
              fontFamily: chartTheme.font.family,
              fontSize: isMobile ? 11 : chartTheme.font.sizes.base,
              padding: isMobile ? "8px" : undefined,
            }}
            formatter={(value: number | undefined) =>
              value !== undefined ? formatCurrency(value) : ""
            }
            labelFormatter={(label) => formatDate(label)}
          />
          <Legend
            wrapperStyle={{
              fontFamily: chartTheme.font.family,
              fontSize: isMobile ? 11 : chartTheme.font.sizes.base,
              color: chartTheme.colors.text,
              paddingTop: isMobile ? "12px" : "20px",
            }}
          />
          <Line
            type="monotone"
            dataKey="revenue"
            name={t("charts.revenue.revenue") || "Revenue"}
            stroke={chartTheme.colors.primary}
            strokeWidth={2}
            dot={{ fill: chartTheme.colors.primary, r: isMobile ? 2 : 4 }}
            activeDot={{ r: isMobile ? 4 : 6 }}
          />
          <Line
            type="monotone"
            dataKey="net_profit"
            name={t("charts.revenue.net_profit") || "Net Profit"}
            stroke={chartTheme.colors.secondary}
            strokeWidth={2}
            dot={{ fill: chartTheme.colors.secondary, r: isMobile ? 2 : 4 }}
            activeDot={{ r: isMobile ? 4 : 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RevenueChart;
